import React, { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Enter your remarks...',
  className = '',
  disabled = false 
}) => {
  // Custom toolbar configuration
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  }), []);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'align', 'link'
  ];

  // Custom styles for dark mode and theme integration
  const editorStyles = `
    .ql-toolbar {
      border-top: 1px solid #e5e7eb;
      border-left: 1px solid #e5e7eb;
      border-right: 1px solid #e5e7eb;
      border-bottom: none;
      background-color: #f9fafb;
    }
    
    .dark .ql-toolbar {
      border-color: #4b5563;
      background-color: #374151;
    }
    
    .ql-container {
      border-bottom: 1px solid #e5e7eb;
      border-left: 1px solid #e5e7eb;
      border-right: 1px solid #e5e7eb;
      border-top: none;
      background-color: white;
      min-height: 120px;
    }
    
    .dark .ql-container {
      border-color: #4b5563;
      background-color: #374151;
      color: white;
    }
    
    .ql-editor {
      min-height: 120px;
      max-height: 300px;
      overflow-y: auto;
      color: #111827;
    }
    
    .dark .ql-editor {
      color: white;
    }
    
    .ql-editor.ql-blank::before {
      color: #9ca3af;
      font-style: normal;
    }
    
    .dark .ql-editor.ql-blank::before {
      color: #6b7280;
    }
    
    .ql-toolbar .ql-stroke {
      stroke: #6b7280;
    }
    
    .dark .ql-toolbar .ql-stroke {
      stroke: #d1d5db;
    }
    
    .ql-toolbar .ql-fill {
      fill: #6b7280;
    }
    
    .dark .ql-toolbar .ql-fill {
      fill: #d1d5db;
    }
    
    .ql-toolbar button:hover {
      background-color: #f3f4f6;
    }
    
    .dark .ql-toolbar button:hover {
      background-color: #4b5563;
    }
    
    .ql-toolbar button.ql-active {
      background-color: #0d9488;
      color: white;
    }
    
    .dark .ql-toolbar button.ql-active {
      background-color: #14b8a6;
    }
    
    .ql-toolbar .ql-picker-label {
      color: #6b7280;
    }
    
    .dark .ql-toolbar .ql-picker-label {
      color: #d1d5db;
    }
    
    .ql-toolbar .ql-picker-options {
      background-color: white;
      border: 1px solid #e5e7eb;
    }
    
    .dark .ql-toolbar .ql-picker-options {
      background-color: #374151;
      border-color: #4b5563;
    }
    
    .ql-toolbar .ql-picker-item {
      color: #6b7280;
    }
    
    .dark .ql-toolbar .ql-picker-item {
      color: #d1d5db;
    }
    
    .ql-toolbar .ql-picker-item:hover {
      background-color: #f3f4f6;
    }
    
    .dark .ql-toolbar .ql-picker-item:hover {
      background-color: #4b5563;
    }
  `;

  const quillRef = useRef(null);

  return (
    <div className={`rich-text-editor ${className}`}>
      <style>{editorStyles}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={disabled}
        modules={modules}
        formats={formats}
        style={{
          backgroundColor: 'transparent'
        }}
      />
      
      {/* Character count */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 border-t-0 rounded-b-xl text-xs text-gray-500 dark:text-gray-400">
        <div className="flex justify-between items-center">
          <span>Rich text editor</span>
          <span>{(value || '').replace(/<[^>]*>/g, '').length} characters</span>
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;
